import {
  ConfirmationBase,
  ConfirmationBasePatchRequest,
} from "../types/confirmation";
import { DatePickerField } from "@/shared/components/field/date-picker-field";
import { useErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { InputField } from "@/shared/components/field/input-field";
import t from "@/lang/lang";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { FieldSeparator } from "@/shared/components/ui/field";
import { SourcesParams } from "../../cpy/api/cpy";
import { CpyName } from "../../cpy/types/entity-names";
import { RFC3339timestamp, UUID } from "@bitory-gmbh/nidavellir";
import { CpySelectField } from "@/shared/components/field/cpy-select-field";

export function ConfirmationBaseForm({
  confirmationId,
  base,
  onBasePatch,
  error,
  cpyDestinationEntityName,
  cpyOfferSourceEntityName,
  cpyOrderSourceEntityName,
  cpyOrderSourcesParams,
  cpyOfferSourcesParams,
  disabled,
}: {
  confirmationId: UUID;
  base: ConfirmationBase;
  onBasePatch: (c: ConfirmationBasePatchRequest) => void;
  error: unknown;
  cpyDestinationEntityName: CpyName;
  cpyOrderSourceEntityName: CpyName;
  cpyOfferSourceEntityName: CpyName;
  cpyOrderSourcesParams: SourcesParams;
  cpyOfferSourcesParams: SourcesParams;
  disabled?: boolean;
}) {
  const { notReadyError, resetNotReadyErrorField } = useErrDocumentNotReady();

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <DatePickerField
        label={t("Datum Auftragsbestätigung")}
        nullable={false}
        date={base.date}
        onDateChange={(date: RFC3339timestamp) => onBasePatch({ date })}
        disabled={disabled}
      />
      <FieldSeparator className="col-span-full" />
      <CpySelectField
        label={t("Auftragseingang")}
        description={t(
          "Die aktuellen Daten werden durch die Auswahl überschrieben.",
        )}
        warning=""
        value={base.orderId}
        onValueReset={() => onBasePatch({ orderId: null })}
        destinationId={confirmationId}
        destinationEntityName={cpyDestinationEntityName}
        sourceEntityName={cpyOrderSourceEntityName}
        sourcesParams={cpyOrderSourcesParams}
        disabled={disabled}
      />
      <InputField
        label={t("Referenz Auftragseingang")}
        description={t("z.B. Kundenreferenz auf Bestellung")}
        value={base.orderReference}
        onChange={(e) => {
          resetNotReadyErrorField("orderReference");
          onBasePatch({ orderReference: e.target.value });
        }}
        errors={[
          ...inputValidationErrorMessages(error, "orderReference"),
          ...inputValidationErrorMessages(notReadyError, "orderReference"),
        ]}
        disabled={disabled}
      />
      <DatePickerField
        nullable
        date={base.orderDate}
        onDateChange={(orderDate) => {
          resetNotReadyErrorField("orderDate");
          onBasePatch({ orderDate: orderDate ?? null });
        }}
        label={t("Datum Auftragseingang")}
        errors={[
          ...inputValidationErrorMessages(error, "orderDate"),
          ...inputValidationErrorMessages(notReadyError, "orderDate"),
        ]}
        disabled={disabled}
      />
      <FieldSeparator className="col-span-full" />
      <CpySelectField
        label={t("Angebotseingang")}
        description={t(
          "Die aktuellen Daten werden durch die Auswahl überschrieben.",
        )}
        warning=""
        value={base.offerId}
        onValueReset={() => onBasePatch({ offerId: null })}
        destinationId={confirmationId}
        destinationEntityName={cpyDestinationEntityName}
        sourceEntityName={cpyOfferSourceEntityName}
        sourcesParams={cpyOfferSourcesParams}
        disabled={disabled}
      />
      <InputField
        label={t("Referenz Angebot")}
        description={t("z.B. Angebotsnummer")}
        value={base.offerReference}
        onChange={(e) => {
          resetNotReadyErrorField("offerReference");
          onBasePatch({ offerReference: e.target.value });
        }}
        errors={[
          ...inputValidationErrorMessages(error, "offerReference"),
          ...inputValidationErrorMessages(notReadyError, "offerReference"),
        ]}
        disabled={disabled}
      />
      <DatePickerField
        nullable
        date={base.offerDate}
        onDateChange={(offerDate) => {
          resetNotReadyErrorField("offerDate");
          onBasePatch({ offerDate: offerDate ?? null });
        }}
        label={t("Datum Angebot")}
        errors={[
          ...inputValidationErrorMessages(error, "offerDate"),
          ...inputValidationErrorMessages(notReadyError, "offerDate"),
        ]}
        disabled={disabled}
      />
    </div>
  );
}
